import React, { useState } from 'react';

import { getEngagementContinuation } from '../../../../api/v2/program_insights';
import useProgramInsightsMetric from '../use_program_insights_metric';
import MetricCard from '../metric_card';
import KpiCards from '../kpi_cards';
import ConsentDecisionInsights from '../consent_decision_insights';
import styles from './index.module.css';

const DESCRIPTION =
  'Journey of participants who joined during pre-consent (recruitment): how many moved into continued ' +
  'mentorship after enrollment versus stopped, remained undecided, screen failed, or did not continue.';

// Outcome colors, strongest continuation first.
const OUTCOME_COLORS = {
  enrolled_still_in_mentorship: 'var(--pp-ocean-tide)',
  enrolled_not_continued: 'var(--pp-fresh-mint)',
  screen_failed: 'var(--pp-slate-blue)',
  decided_not_to_continue: 'var(--pp-navy-mid)',
  undecided: 'var(--pp-pale-aqua)',
};

export default function EngagementContinuation({ filters }) {
  const { data, status } = useProgramInsightsMetric(getEngagementContinuation, filters);
  const [drillInOpen, setDrillInOpen] = useState(false);

  // The metric only applies to recruitment-and-retention studies; hide the card entirely otherwise.
  // Keep hidden whenever we know recruitment_and_retention is false, regardless of request status.
  if (data && data.recruitment_and_retention === false) {
    return null;
  }

  const hasData = data && data.totals.pre_consent_participants > 0;
  const maxCount = hasData ? Math.max(...data.outcomes.map((outcome) => outcome.count), 1) : 1;

  return (
    <MetricCard
      title='Engagement Continuation'
      subtitle='Recruitment-to-retention journey outcomes'
      description={DESCRIPTION}
      dependsOnFeed
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No pre-consent participants match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          <KpiCards
            cards={[
              { label: 'Pre-Consent Participants', value: data.totals.pre_consent_participants.toLocaleString() },
              { label: 'Enrolled Participants', value: data.totals.enrolled_participants.toLocaleString() },
            ]}
          />

          <p className={styles.sectionLabel}>Participant journey outcomes</p>

          <ul className={styles.outcomes}>
            {data.outcomes.map((outcome) => (
              <li key={outcome.key} className={styles.outcome}>
                <span className={styles.outcomeLabel}>{outcome.label}</span>
                <div className={styles.track}>
                  <div
                    className={styles.bar}
                    style={{
                      width: `${(outcome.count / maxCount) * 100}%`,
                      backgroundColor: OUTCOME_COLORS[outcome.key],
                    }}
                  />
                </div>
                <span className={styles.outcomeCount}>{outcome.count.toLocaleString()}</span>
              </li>
            ))}
          </ul>

          <button
            type='button'
            className={styles.drillInToggle}
            aria-expanded={drillInOpen}
            onClick={() => setDrillInOpen((open) => !open)}
          >
            {drillInOpen
              ? 'Hide consent decision insights'
              : 'Why didn’t they continue? See consent decision insights →'}
          </button>

          {drillInOpen && <ConsentDecisionInsights filters={filters} />}
        </>
      )}
    </MetricCard>
  );
}
